import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'gadkot_shop_cart';

const ShopCartContext = createContext(null);

function readStoredCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStoredCart(lines) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  } catch {
    // ignore
  }
}

export function ShopCartProvider({ children }) {
  const [lines, setLines] = useState(() => readStoredCart());

  useEffect(() => {
    writeStoredCart(lines);
  }, [lines]);

  const addItem = useCallback((product, quantity = 1) => {
    if (!product?.id) return;
    const qty = Math.max(1, Math.min(20, Number(quantity) || 1));
    setLines((prev) => {
      const idx = prev.findIndex((l) => l.productId === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          quantity: Math.min(20, next[idx].quantity + qty),
        };
        return next;
      }
      return [
        ...prev,
        {
          productId: product.id,
          slug: product.slug,
          name: product.name,
          pricePaise: product.pricePaise,
          image: product.image,
          stock: product.stock,
          quantity: qty,
        },
      ];
    });
  }, []);

  const setQuantity = useCallback((productId, quantity) => {
    const qty = Math.max(0, Math.min(20, Number(quantity) || 0));
    setLines((prev) => {
      if (qty === 0) return prev.filter((l) => l.productId !== productId);
      return prev.map((l) => (l.productId === productId ? { ...l, quantity: qty } : l));
    });
  }, []);

  const removeItem = useCallback((productId) => {
    setLines((prev) => prev.filter((l) => l.productId !== productId));
  }, []);

  const clearCart = useCallback(() => setLines([]), []);

  const cartCount = useMemo(
    () => lines.reduce((sum, l) => sum + l.quantity, 0),
    [lines]
  );

  const cartTotalPaise = useMemo(
    () => lines.reduce((sum, l) => sum + l.pricePaise * l.quantity, 0),
    [lines]
  );

  const value = useMemo(
    () => ({
      lines,
      cartCount,
      cartTotalPaise,
      addItem,
      setQuantity,
      removeItem,
      clearCart,
    }),
    [lines, cartCount, cartTotalPaise, addItem, setQuantity, removeItem, clearCart]
  );

  return <ShopCartContext.Provider value={value}>{children}</ShopCartContext.Provider>;
}

export function useShopCart() {
  const ctx = useContext(ShopCartContext);
  if (!ctx) {
    throw new Error('useShopCart must be used within ShopCartProvider');
  }
  return ctx;
}

export default ShopCartContext;
